import CovidTestModalItem from "@components/CovidTestModalItem";
import {FeatureFlag} from "@services/featureFlagConstants";
import {ApptSlot} from "@services/monolith/availability";
import {useTranslation} from "ni18n";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {actions, useTypedSelector} from "src/store";
import {selectSelectedRegion} from "src/store/slices/userLocation";
import {v4} from "uuid";

import {ApptReasonId} from "../../constants/apptReasons";
import {SpecialtyId} from "../../constants/specialtyIds";
import {useResultExtensions} from "../../hooks/useCareEntryExtensions";
import {useFeatureFlag} from "../../hooks/useFeatureFlags";
import {usePrices} from "../../hooks/usePrices";
import {fetchCachedSlotAround} from "../../utils/fetchCachedSlotAround";
import {fetchPricingData} from "../../utils/fetchPricingData";
import {filterLocationsBySpecialtyId} from "../../utils/filterLocationsBySpecialtyId";
import {getPriceByApptReasonId} from "../../utils/getPriceByApptReasonId";
import {getSchedulingRedirectUrl} from "../../utils/getRedirectUrl";
import {locationsByRegion} from "../../utils/locationsByRegion";
import {v5Pages} from "../_common/_constants";

export type CovidModalItem = {
  icon?: string;
  title: string;
  subTitle: string;
  howLong?: string;
  price?: string | number | null;
  slot?: Partial<ApptSlot> | null;
  slug: string;
  reasonId?: string;
  specialtyId: string;
  href?: string;
  travel?: boolean;
  flowId?: string;
  specialtyIdInCategory?: string;
};

const TRAVEL = "TRAVEL";
const NONTRAVEL = "NONTRAVEL";
const LOCATIONS_LIMIT = 5; // count of locations with specialty

const CovidTestModal = ({
  covidLocationId,
  isCovidTestingPage,
}: {
  covidLocationId?: string;
  isCovidTestingPage?: boolean;
}): JSX.Element => {
  const i18n = useTranslation();
  const {locations, locationsSorted} = useTypedSelector(({config}) => config);
  const selectedRegion = useTypedSelector(selectSelectedRegion);
  const pricingData = useTypedSelector(state => state.regionPricing);
  const hasPricingData = pricingData.tier1.length !== 0;
  const locsByRegion = useMemo(
    () => locationsByRegion(locations, selectedRegion),
    [locations, selectedRegion],
  );

  useEffect(() => {
    if (!hasPricingData) fetchPricingData().then(data => dispatch(actions.setRegionPricing(data)));
  }, [hasPricingData]);

  const dispatch = useDispatch();
  const enableCache = useFeatureFlag<boolean>(FeatureFlag.GROWTH_CACHED_SLOTS_ENABLED);

  const [slotsFetched, setSlotsFetched] = useState(false);

  const preselectedLocations = useMemo(
    () => locations.filter(l => l.id === covidLocationId),
    [covidLocationId, locations],
  );
  const location = preselectedLocations[0];

  const titles = {[NONTRAVEL]: i18n.t("COVID Testing"), [TRAVEL]: i18n.t("COVID Travel Testing")};

  const reasonExtensions = useResultExtensions();

  const prices = usePrices(location?.region?.slug);

  const items: CovidModalItem[] = useMemo(
    () =>
      [
        {
          icon: reasonExtensions[ApptReasonId.COVID_SICK_VISIT].icon,
          title: i18n.t("COVID Sick Visit"),
          subTitle: i18n.t(
            "Full provider evaluation for patients with symptoms, including any necessary testing.",
          ),
          price: i18n.t("Covered by insurance"),
          howLong: reasonExtensions[ApptReasonId.COVID_SICK_VISIT].turnaround,
          reasonId: ApptReasonId.COVID_SICK_VISIT,
          specialtyId: SpecialtyId.COVID_TESTING_MOBILE,
          slug: "covid-sick-visit",
        },
        {
          icon: reasonExtensions[ApptReasonId.COVID_TESTING_NO_SYMPTOMS].icon,
          title: i18n.t("COVID Exposure Screening"),
          subTitle: i18n.t(
            "Full provider evaluation for patients with an exposure risk, including any necessary testing.",
          ),
          price: i18n.t("Covered by insurance"),
          howLong: reasonExtensions[ApptReasonId.COVID_TESTING_NO_SYMPTOMS].turnaround,
          reasonId: ApptReasonId.COVID_TESTING_NO_SYMPTOMS,
          specialtyId: SpecialtyId.COVID_TESTING_MOBILE,
          slug: "covid-exposure-screening",
        },
        {
          icon: "rapid-hepatitis-c-screening",
          title: i18n.t("Same-Day NAAT"),
          subTitle: i18n.t("No provider evaluation. Samples processed on-site."),
          howLong: reasonExtensions[ApptReasonId.COVID_TRAVEL_CLEARANCE_TESTING].turnaround,
          reasonId: ApptReasonId.COVID_TRAVEL_CLEARANCE_TESTING,
          specialtyId: SpecialtyId.COVID_TRAVEL_CLEARANCE,
          slug: "same-day-naat",
          specialtyIdInCategory: SpecialtyId.COVID_TESTING_MOBILE, // We want to show this item under Non-Travel list but it actually belongs to Travel items. Programatically, we need a way to know which category it belongs to filter it out.
        },
        {
          travel: true,
          icon: "rapid-hepatitis-c-screening",
          title: i18n.t("Same-Day NAAT"),
          subTitle: i18n.t("No provider evaluation. Samples processed on-site."),
          howLong: reasonExtensions[ApptReasonId.COVID_TRAVEL_CLEARANCE_TESTING].turnaround,
          reasonId: ApptReasonId.COVID_TRAVEL_CLEARANCE_TESTING,
          specialtyId: SpecialtyId.COVID_TRAVEL_CLEARANCE,
          slug: "same-day-naat",
        },
        {
          travel: true,
          icon: reasonExtensions[ApptReasonId.NEXT_DAY_RT_PCR].icon,
          title: i18n.t("Next-Day RT-PCR"),
          subTitle: i18n.t(
            "No provider evaluation. Samples processed in a lab with expedited results.",
          ),
          howLong: reasonExtensions[ApptReasonId.NEXT_DAY_RT_PCR].turnaround,
          reasonId: ApptReasonId.NEXT_DAY_RT_PCR,
          specialtyId: SpecialtyId.COVID_TRAVEL_CLEARANCE,
          slug: "next-day-rt-pcr",
        },
        {
          travel: true,
          icon: "rapid-hepatitis-c-screening",
          title: i18n.t("Same-Day Antigen"),
          subTitle: i18n.t("No provider evaluation. Samples processed on-site."),
          howLong: reasonExtensions[ApptReasonId.SAME_DAY_ANTIGEN].turnaround,
          reasonId: ApptReasonId.SAME_DAY_ANTIGEN,
          specialtyId: SpecialtyId.COVID_TRAVEL_CLEARANCE,
          slug: "same-day-antigen",
          price: reasonExtensions[ApptReasonId.SAME_DAY_ANTIGEN].price,
        },
        {
          travel: true,
          icon: "rapid-hepatitis-c-screening",
          title: i18n.t("Same-Day RT-PCR"),
          subTitle: i18n.t("No provider evaluation. Samples processed on-site."),
          howLong: reasonExtensions[ApptReasonId.SAME_DAY_RT_PCR].turnaround,
          reasonId: ApptReasonId.SAME_DAY_RT_PCR,
          specialtyId: SpecialtyId.COVID_TRAVEL_CLEARANCE,
          slug: "same-day-rt-pcr",
          price: reasonExtensions[ApptReasonId.SAME_DAY_RT_PCR].price,
        },
      ].map(item => ({
        ...item,
        price: item.price || getPriceByApptReasonId(item.reasonId || "", prices),
        href: location
          ? getSchedulingRedirectUrl({
              practiceId: location.practiceId,
              locationId: location.id,
              specialtyId: item.specialtyId,
              apptReasonId: item.reasonId,
              flowId: v4(),
            })
          : `${v5Pages.getCareRoot}/${item.slug}`,
      })),
    [i18n, location, prices, reasonExtensions],
  );

  const [itemsFinal, setItemsFinal] = useState<CovidModalItem[]>(items);

  const fetchAndAppendSlots = useCallback(async () => {
    const withSlots = await Promise.all(
      items.map(async item => {
        const {reasonId, specialtyId} = item;
        const closestLocationsBySpecialty = filterLocationsBySpecialtyId(
          location ? preselectedLocations : locsByRegion,
          specialtyId,
          LOCATIONS_LIMIT,
        );
        const slot = await fetchCachedSlotAround({
          locations: closestLocationsBySpecialty,
          specialtyId,
          reasonId,
          locationCount: LOCATIONS_LIMIT,
        });

        return {
          ...item,
          slot: slot?.time ? slot : null,
        };
      }),
    );

    setItemsFinal(withSlots);
    setSlotsFetched(true);
  }, [items, location, locsByRegion, preselectedLocations]);

  useEffect(() => {
    if (locationsSorted && enableCache) {
      fetchAndAppendSlots();
    }

    if (!enableCache) {
      setSlotsFetched(true);
    }
  }, [enableCache, fetchAndAppendSlots, locationsSorted]);

  const itemsGrouped = itemsFinal.groupBy(({travel}) => (travel ? TRAVEL : NONTRAVEL));

  if (!slotsFetched) return <span>{i18n.t("Loading...")}</span>;

  return (
    <div>
      {itemsGrouped.getKeys().map(g => {
        const items = itemsGrouped[g]
          .filter(({slot}) => !location || slot?.time)
          .filter(
            ({specialtyIdInCategory}) =>
              !isCovidTestingPage || (isCovidTestingPage && !specialtyIdInCategory),
          );
        if (!items.length) return null;

        // check if location has items with specialtyIds (only in location page)
        if (location) {
          const isSpecialtyIdsInLocation = items.every(it => {
            const specialtyId = it.specialtyIdInCategory || it.specialtyId;
            return location.specialtyIds.includes(specialtyId);
          });
          if (!isSpecialtyIdsInLocation) return null;
        }

        return (
          // @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ NONTRAVEL: string; TRAVEL: string; }'.
          <div key={titles[g]} className={!isCovidTestingPage ? `mt8` : ""}>
            {/* @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ NONTRAVEL: string; TRAVEL: string; }'. */}
            {!isCovidTestingPage && <h2 className="fs24">{titles[g]}</h2>}
            <ul>
              {items
                .map((item, index) =>
                  item.href ? (
                    <CovidTestModalItem
                      {...item}
                      href={item.href}
                      key={item.title}
                      index={index}
                      isCovidTestingPage={isCovidTestingPage}
                    />
                  ) : null,
                )
                .compact()}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default CovidTestModal;
